/// <reference path="../../typings/browser.d.ts" />

namespace Application.Controllers {
    'use strict';

    import IVno = Models.IVno;
    import IUser = Models.IUser;
    import IUserGroup = Models.IUserGroup;
    import UserGroup = Models.UserGroup;

    interface IUserGroupRouteParamsService extends ng.route.IRouteParamsService {
        id: number;
    }

    interface IAux {
        isAdmin: boolean;
    }

    interface ILogoUtils {
        sourceImage: string;
        croppedImage: string;
        bounds: {
            left: number;
            right: number;
            top: number;
            bottom: number;
        };
        editionMode: boolean;
        size: {
            width: number;
            height: number;
        };
        toggleLogoEditionMode: () => void;
        saveLogo: () => ng.IPromise<IVno>;
        removeLogo: () => ng.IPromise<IVno>;
        resetBounds: () => void;
    }

    export class AdminVnoController {
        public static $name: string = "adminVnoController";
        public static $inject: string[] = ['vnoService',
                                           '$routeParams',
                                           'userService',
                                           'userGroupService',
                                           'ModalService',
                                           'aclService',
                                           '$q',
                                           '$window',
                                           '$location',
                                           '$rootScope',
                                           '$scope',
                                           ];

        public vno: IVno;
        public groups: IUserGroup[];
        public isAdmin: boolean = false;
        public showDisabled: boolean = false;

        private watchers: Function[] = [];
        private vnoObservable: Function = undefined;
        private logo: ILogoUtils;

        constructor(private vnoService: Services.VnoService,
                    private $routeParams: IUserGroupRouteParamsService,
                    private userService: Services.UserService,
                    private userGroupService: Services.UserGroupService,
                    private modalService: ngModal.IModalService,
                    private aclService: Services.AclService,
                    private $q: ng.IQService,
                    private $window: ng.IWindowService,
                    private $location: ng.ILocationService,
                    private $rootScope: ng.IRootScopeService,
                    private $scope: ng.IScope) {
            this.init();
            this.$scope.$on('$destroy', () => {
                if (this.vnoObservable !== undefined)
                    this.vnoObservable();
                this.watchers.forEach((w: Function) => w());
            });
            this.vnoObservable = this.$rootScope.$on(Services.VnoService.VNO_CHANGED, () => {
                this.getVnoData();
            });

            this.logo = {
                sourceImage: this.vno && this.vno.logo ? this.vno.logo : undefined,
                croppedImage: undefined,
                bounds: {
                    left: undefined,
                    right: undefined,
                    top: undefined,
                    bottom: undefined,
                },
                editionMode: false,
                size: {
                    width: 210,
                    height: 48,
                },
                toggleLogoEditionMode: () => {
                    this.logo.editionMode = !this.logo.editionMode;
                    this.logo.resetBounds();
                },
                saveLogo: () => {
                    const copy: IVno = Common.Utils.clone(this.vno);
                    copy.logo = this.logo.croppedImage ? this.logo.croppedImage : copy.logo;
                    return this.vnoService.save(copy).finally(() => {
                        this.logo.editionMode = false;
                        this.logo.resetBounds();
                    });
                },
                removeLogo: () => {
                    return this.modalService.showModal({
                        templateUrl: Controllers.ConfirmModalController.templateURL,
                        controller: Controllers.ConfirmModalController.$name,
                        inputs: {
                            title: 'REMOVE LOGO',
                            description: "This action will remove the logo permanently.",
                            name: this.vno.name,
                            type: 'information',
                        },
                    }).then((modal: ngModal.IModalObject) => {
                        let modalElem: UIkit.ModalElement = UIkit.modal(Controllers.ConfirmModalController.ID,
                            {bgclose: false, modal: false, keyboard: false});
                        modalElem.show();
                        return modal.close.then((result: Object) => {
                            modalElem.hide();
                            if (result) {
                                this.vno.logo = this.logo.croppedImage = this.logo.sourceImage = undefined;
                                return this.vnoService.save(this.vno).finally(() => {
                                    this.logo.resetBounds();
                                });
                            }
                        });
                    });
                },
                resetBounds: () => {
                    this.logo.bounds = {
                        left: undefined,
                        right: undefined,
                        top: undefined,
                        bottom: undefined,
                    };
                },
            };
            this.watchers.push(
                this.$scope.$watch('aoc.logo.sourceImage', () => {
                    this.logo.resetBounds();
                }),
            );
        }

        public launchEditionModal(): void {
            if (this.vno === undefined)
                return;
            this.modalService.showModal({
                templateUrl: Controllers.AdminVnoModalController.templateUrl,
                controller: Controllers.AdminVnoModalController.$name,
                inputs: {
                    title: 'Edit VNO',
                    vno: angular.copy(this.vno),
                    users: undefined,
                },
            }).then((modal: ngModal.IModalObject) => {
                let modalElem: UIkit.ModalElement = UIkit.modal(Controllers.AdminVnoModalController.id,
                    {bgclose: false, modal: false, keyboard: false});
                modalElem.show();
                modal.close.then((response: Controllers.IAdminVnoModalResponse) => {
                    modalElem.hide();
                    if (response !== undefined) {
                        this.vno.name = response.name;
                        this.vnoService.save(this.vno).then((updatedVno: Models.IVno) => {
                            this.getVnoData();
                        });
                    }
                });
            });
        }

        public launchRemoveModal(): void {
            if (this.vno === undefined)
                return;
            this.modalService.showModal({
                templateUrl: Controllers.ConfirmModalController.templateURL,
                controller: Controllers.ConfirmModalController.$name,
                inputs: {
                    title: 'Delete VNO',
                    description: `You are about to delete the vno ${this.vno.name}. This action cannot be undone.`,
                    name: undefined,
                    type: 'information',
                },
            }).then((modal: ngModal.IModalObject) => {
                let modalElem: UIkit.ModalElement = UIkit.modal(Controllers.ConfirmModalController.ID,
                    {bgclose: false, modal: false, keyboard: false});
                modalElem.show();
                modal.close.then((result: Object) => {
                    modalElem.hide();
                    if (result) {
                        this.vnoService.deleteVno(this.vno.id).then((deleted: boolean) => {
                            if (deleted) {
                                this.$location.path(`/admin/vno/`);
                            } else {
                                Common.Utils.alertOnError("Cannot remove VNO");
                            }
                        });
                    }
                });
            });
        }

        public launchUsersListModal(type: 'new' | 'existing'): void {
            if (type === 'new') {
                this.launchNewUserModal();
            } else {
                this.launchExistingUserModal();
            }
        }

        public launchEditUsersListModal(user: Models.IUser): void {
            this.launchCreateEditUserModal('Edit user', user);
        }

        public changeAdminRole(user: Models.IUser, action: 'add' | 'remove'): void {
            if (action === 'add') {
                this.vnoService.assignAdminToVno(user, this.vno).then(() => {
                    this.userService.refreshUser(user.id);
                    this.getVnoData();
                });
            } else {
                this.vnoService.demoteAdminFromVno(user, this.vno).then(() => {
                    this.userService.refreshUser(user.id);
                    this.getVnoData();
                });
            }
        }

        public isCurrentUser(user: Models.IUser): boolean {
            if (user === undefined || this.userService.getCurrentUser() === undefined)
                return false;
            return user.id === this.userService.getCurrentUser().id;
        }

        public removeUserFromVno(user: Models.IUser): void {
            this.modalService.showModal({
                templateUrl: Controllers.ConfirmModalController.templateURL,
                controller: Controllers.ConfirmModalController.$name,
                inputs: {
                    title: 'Remove user from VNO',
                    description: `You are about to remove user ${user.username} from this VNO. This action cannot be undone.`,
                    name: undefined,
                    type: 'information',
                },
            }).then((modal: ngModal.IModalObject) => {
                let modalElem: UIkit.ModalElement = UIkit.modal(Controllers.ConfirmModalController.ID,
                    {bgclose: false, modal: false, keyboard: false});
                modalElem.show();
                modal.close.then((result: Object) => {
                    modalElem.hide();
                    if (result) {
                        this.vnoService.removeUserFromVno(user, this.vno).then((removedUser: IUser) => {
                            this.userService.refreshUser(user.id);
                            Common.Utils.alertOnSuccess(`User ${removedUser.username} removed from VNO.`);
                        });
                    }
                });
            });
        }

        public removeGroup(group: Models.IUserGroup): void {
            this.modalService.showModal({
                templateUrl: Controllers.ConfirmModalController.templateURL,
                controller: Controllers.ConfirmModalController.$name,
                inputs: {
                    title: 'Delete group from VNO',
                    description: `You are about to delete group ${group.name} from this VNO. This action cannot be undone.`,
                    name: undefined,
                    type: 'information',
                },
            }).then((modal: ngModal.IModalObject) => {
                let modalElem: UIkit.ModalElement = UIkit.modal(Controllers.ConfirmModalController.ID,
                    {bgclose: false, modal: false, keyboard: false});
                modalElem.show();
                modal.close.then((result: Object) => {
                    modalElem.hide();
                    if (result) {
                        this.vnoService.removeVnoGroup(this.vno.id, group.id as number);
                    }
                });
            });
        }

        public enableGroup(group: Models.IUserGroup): void {
            group.enabled = true;
            this.vnoService.editVnoGroup(group, this.vno.id).then((result: Models.IUserGroup) => {
                group.enabled = result.enabled;
            });
        }

        public disableGroup(group: Models.IUserGroup): void {
            group.enabled = false;
            this.vnoService.editVnoGroup(group, this.vno.id).then((result: Models.IUserGroup) => {
                group.enabled = result.enabled;
            });
        }

        public launchUserGroupModal(): void {
            let emptyGroup: IUserGroup = UserGroup.createEmpty();
            emptyGroup.enabled = true;
            this.modalService.showModal({
                templateUrl: Controllers.AdminGroupModalController.templateURL,
                controller: Controllers.AdminGroupModalController.$name,
                inputs: {
                    title: `Create group for ${this.vno.name} VNO`,
                    group: emptyGroup,
                },
            }).then((modal: ngModal.IModalObject) => {
                let modalElem: UIkit.ModalElement = UIkit.modal(Controllers.AdminGroupModalController.ID,
                    {bgclose: false, modal: false, keyboard: false});
                modalElem.show();
                modal.close.then((group?: IUserGroup) => {
                    modalElem.hide();
                    if (group) {
                        this.vnoService.createVnoGroup(group, this.vno.id);
                    }
                });
            });
        }

        public getVnoGroupsData(): void {
            this.vno.groups.forEach((group: Models.IUserGroup) => {
                this.userGroupService.getUsersInGroup(group.id as number, this.vno.id).then((usersInGroup: Models.IUser[]) => {
                    this.vno.users.forEach((vnoUser: Models.IUser) => {
                        let groupListString: string = 'groupList';
                        if (!vnoUser[groupListString]) {
                            vnoUser[groupListString] = ['All users ' + this.vno.name];
                        }
                        let userIsInGroup: boolean = usersInGroup.some((groupUser: Models.IUser, index: number, array: Models.IUser[]) => {
                            return +vnoUser.id === +groupUser.id; // + sign ensures ids are numbers, not strings
                        });
                        let groupAlreadyInList: boolean = vnoUser[groupListString].some(
                            (groupInListName: string, index: number, array: string[]) => {
                                return groupInListName === group.name;
                        });
                        if (userIsInGroup && !groupAlreadyInList) { 
                            vnoUser[groupListString].push(group.name);
                        }
                    });
                });
            });
        }

        private init(): void {
            this.userService.loadCurrentUser().then(async (user: Models.User) => {
                await this.checkAuth();
                this.getVnoData();
                this.isAdmin = await this.vnoService.isAdminUser(+this.userService.getCurrentUser().id);
            });
        }

        private launchNewUserModal(): void {
            let emptyUser: IUser = Models.User.createEmpty();
            this.launchCreateEditUserModal('Create user', emptyUser);
        }

        private launchCreateEditUserModal(title: string, user: Models.IUser): void {
            this.modalService.showModal({
                templateUrl: Controllers.UserNewEditModalController.templateURL,
                controller: Controllers.UserNewEditModalController.$name,
                inputs: {
                    title: title,
                    user: user,
                    vnos: undefined,
                },
            }).then((modal: ngModal.IModalObject) => {
                let modalElem: UIkit.ModalElement = UIkit.modal(Controllers.UserNewEditModalController.ID,
                    {bgclose: false, modal: false, keyboard: false});
                modalElem.show();
                modal.close.then((result?: IUserEditResponse) => {
                    modalElem.hide();
                    if (result) {
                        this.createOrUpdateUser(result.user);
                    }
                });
            });
        }

        private createOrUpdateUser(user: Models.IUser): void {
            if (user.id) {
                // Update existing user
                this.userService.updateUser(user).then(() => {
                    this.getVnoData();
                    if (!user.enabled && !this.showDisabled) {
                        Common.Utils.alertOnSuccess('User disabled. Switch to \'Show disabled\' to show it', 'top-right');
                    }
                }).catch((reason: ngCustom.IReason) => {
                    this.showErrorModal('Error saving user', 'A user with the same username/email already exists.');
                });
            } else {
                // Create new user
                this.vnoService.createUserIntoVno(user, this.vno).then(() => {
                    this.getVnoData();
                }).catch((reason: ngCustom.IReason) => {
                    this.showErrorModal('Error saving user', 'A user with the same username/email already exists.');
                });
            }
        }

        private showErrorModal(title: string, description: string): void {
            this.modalService.showModal({
                templateUrl: Controllers.ErrorModalController.templateURL,
                controller: Controllers.ErrorModalController.$name,
                inputs: {
                    title,
                    description,
                },
            }).then((modal: ngModal.IModalObject) => {
                let modalElem: UIkit.ModalElement = UIkit.modal(Controllers.ErrorModalController.ID,
                    {bgclose: false, modal: false, keyboard: false});
                modalElem.show();
                modal.close.then((result: Object) => {
                    modalElem.hide();
                });
            });
        }

        private launchExistingUserModal(): void {
            this.userService.findAll().then((users: Models.IUser[]) => {
                const usersNotInVno: Models.IUser[] = users.filter((user: Models.IUser) => {
                    return user.organizationId !== this.$routeParams.id;
                });
                const usersInVno: Models.IUser[] = users.filter((user: Models.IUser) => {
                    return user.organizationId === this.$routeParams.id;
                });
                return this.manageUsersModal(usersNotInVno, usersInVno);
            }).then((result: { usersToAdd: IUser[], usersToDelete: IUser[] }) => {
                const hasUsersToAdd: boolean = result && result.usersToAdd && result.usersToAdd.length > 0;
                const hasUsersToRemove: boolean = result && result.usersToDelete && result.usersToDelete.length > 0;
                if (hasUsersToAdd || hasUsersToRemove) {
                    this.modalService.showModal({
                        templateUrl: Controllers.ConfirmModalController.templateURL,
                        controller: Controllers.ConfirmModalController.$name,
                        inputs: {
                            title: 'Change users VNO',
                            description: `This action can not be undone. Do you wish to continue?`,
                            name: undefined,
                            type: 'information',
                        },
                    }).then((modal: ngModal.IModalObject) => {
                        let modalElem: UIkit.ModalElement = UIkit.modal(Controllers.ConfirmModalController.ID,
                            {bgclose: false, modal: false, keyboard: false});
                        modalElem.show();
                        modal.close.then((modalResult: Object) => {
                            modalElem.hide();
                            if (modalResult) {
                                this.updateUsersFromVno(result.usersToAdd, result.usersToDelete);
                            }
                        });
                    });
                }
            });
        }

        private manageUsersModal(usersToSelect: Models.IUser[], 
                                 usersSelected: Models.IUser[]): ng.IPromise<{ usersToAdd: IUser[], 
                                 usersToDelete: IUser[] }> {
            return this.vnoService.findAll().then((vnos: Models.Vno[]) => {

                const vnosById: { id: number, vno: Models.Vno } = {} as { id: number, vno: Models.Vno };
                vnos.forEach((vno: Models.Vno) => {
                    vnosById[vno.id] = vno;
                });

                const objectsToSelect: Object[] = [];
                usersToSelect.forEach((user: Models.IUser) => {
                    const objectToSelect: Object = user;
                    if (user.organizationId) {
                        objectToSelect['currentVno'] = vnosById[user.organizationId].name;
                    }
                    objectsToSelect.push(objectToSelect);
                });

                const objectsSelected: Object[] = [];
                usersSelected.forEach((user: Models.IUser) => {
                    const objectSelected: Object = user;
                    if (user.organizationId) {
                        objectSelected['currentVno'] = vnosById[user.organizationId].name;

                    }
                    objectsSelected.push(objectSelected);
                });

                return this.modalService.showModal({
                    templateUrl: Controllers.DualListModalController.templateUrl,
                    controller: Controllers.DualListModalController.$name,
                    inputs: {
                        title: `Manage Users for ${this.vno.name}`,
                        titleElements: `Rest of users`,
                        titleSelectedElements: `Users in VNO`,
                        firstColumn: 'Username',
                        secondColumn: 'VNO',
                        elements: objectsToSelect,
                        selectedElements: objectsSelected,
                        idProperty: 'id',
                        nameProperty: 'username',
                        secondColumnProperty: 'currentVno',
                        noMoreToAddText: 'No users to add',
                        noneSelectedText: 'No users selected',
                        secondaryInfoCompare: vnosById[this.$routeParams.id].name,
                        highlightedId: this.userService.getCurrentUser().id,
                        type: Controllers.DualListModalController.userType,
                    },
                }).then((modal: ngModal.IModalObject) => {
                    let modalElem: UIkit.ModalElement = UIkit.modal(Controllers.DualListModalController.ID,
                        {bgclose: false, modal: false, keyboard: false});
                    modalElem.show();
                    return modal.close.then((result?: { selected: IUser[], unselected: IUser[] }) => {
                        modalElem.hide();
                        if (result) {
                            let usersToAdd: IUser[] = Common.Utils.objectsDifference(result.selected, objectsSelected) as IUser[];
                            let usersToDelete: IUser[] = Common.Utils.objectsDifference(result.unselected, objectsToSelect) as IUser[];
                            return {usersToAdd: usersToAdd, usersToDelete: usersToDelete};
                        }
                    });

                });
            });
        }

        private updateUsersFromVno(usersToAdd: IUser[], usersToDelete: IUser[]): void {
            this.vnoService.updateUsersFromVno(usersToAdd, usersToDelete, this.vno)
                .then((users: IUser[]) => {
                    const userIds: number[] = usersToAdd.concat(usersToDelete).map((modifiedUser: IUser) => +modifiedUser.id);
                    this.userService.refreshUsers(userIds).then(() => {
                        this.getVnoData();
                    });
            });
        }

        private getVnoData(): void {
            if (this.$routeParams.id && this.$routeParams.id > 0) {
                this.vnoService.findById(this.$routeParams.id).then((response: IVno) => {
                    this.vno = response;
                    if (this.vno.logo === undefined) {
                        this.vnoService.getVnoLogo(this.vno.id).then((logo: string) => {
                            this.vno.logo = logo;
                            this.logo.sourceImage = logo;
                        });
                    } else {
                        this.logo.sourceImage = this.vno.logo;
                    }
                    this.removeDemiurgo(this.vno);
                    this.vno.users.map((user: Models.IUser) => {
                        (user as Models.IUser & IAux).isAdmin = false;
                        for (let adminUser of this.vno.adminUsers)
                            if (user.id === adminUser.id)
                                (user as Models.IUser & IAux).isAdmin = true;
                    });
                    let defaultGroup: IUserGroup = this.userGroupService.generateDefaultGroup(this.vno);
                    this.groups = this.vno.groups.concat(defaultGroup); // changes to this.vno caused undesired results since it's in memory
                    this.getVnoGroupsData();
                });
            }
        }

        private removeDemiurgo(vno: Models.IVno): void {
            let i: number;
            for (i = 0; i < vno.users.length; i++)
                if (vno.users[i].username === 'demiurgo')
                    break;
            if (i !== undefined && i >= 0 && i < vno.users.length)
                vno.users.splice(i, 1);
        }

        // private isAdminUser(): void {
        //     const roles: string[] = this.vnoService.getUserRoles(+this.userService.getCurrentUser().id); 
        //     // this.userService.getUserRoles(this.userService.getCurrentUser().id as number).then((roles: string[]) => {
        //     for (let role of roles) {
        //         if (role === 'ROLE_ADMIN')
        //             this.isAdmin = true;
        //     }
        //     // });
        // }

        private async checkAuth(): Promise<void> {
            const userId: number = +this.userService.getCurrentUser().id;
            const vnoId: number = this.$routeParams.id;
            if (!(await this.vnoService.isAdminUser(userId))) {
                if (!(await this.vnoService.isVnoAdmin(userId, vnoId))) {
                    this.$location.path(`/`);
                }
            }
        }
    }
}
